import React from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Facebook, Linkedin, Twitter } from 'lucide-react';

export default function SocialMediaGuidelines() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl md:text-5xl font-display font-normal text-jackstone-blue mb-4">
          Social Media Guidelines
        </h1>
        <p className="text-base text-body leading-relaxed">
          Maintain brand consistency across all social media platforms with professional,
          technically-focused content.
        </p>
      </div>

      <div>
        <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-4">
          Platform-Specific Guidelines
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#0A66C2] rounded-lg flex items-center justify-center">
                <Linkedin className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-display font-normal">LinkedIn</h3>
            </div>
            <Badge className="mb-3">Primary Platform</Badge>
            <p className="text-sm text-subtle mb-3">
              Professional content, industry insights, technical articles, and company updates.
            </p>
            <div className="text-xs text-subtle space-y-1">
              <p>• Post frequency: 3-4 times per week</p>
              <p>• Image size: 1200 x 627px</p>
              <p>• Video length: 30-90 seconds</p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#1DA1F2] rounded-lg flex items-center justify-center">
                <Twitter className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-display font-normal">Twitter/X</h3>
            </div>
            <Badge className="mb-3">Secondary Platform</Badge>
            <p className="text-sm text-subtle mb-3">
              Quick updates, industry news, event announcements, and customer highlights.
            </p>
            <div className="text-xs text-subtle space-y-1">
              <p>• Post frequency: 5-7 times per week</p>
              <p>• Image size: 1200 x 675px</p>
              <p>• Character limit: Consider thread format</p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#1877F2] rounded-lg flex items-center justify-center">
                <Facebook className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-display font-normal">Facebook</h3>
            </div>
            <Badge className="mb-3">Supporting Platform</Badge>
            <p className="text-sm text-subtle mb-3">
              Company updates, customer stories, behind-the-scenes content, event coverage.
            </p>
            <div className="text-xs text-subtle space-y-1">
              <p>• Post frequency: 2-3 times per week</p>
              <p>• Image size: 1200 x 630px</p>
              <p>• Video format: Square or vertical</p>
            </div>
          </Card>
        </div>
      </div>

      <Card className="p-6">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
          Profile and Cover Image Guidelines
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-body mb-2">Profile Image</h4>
            <ul className="space-y-2 text-sm text-subtle">
              <li>• Use the Jackstone logo on white or primary blue background</li>
              <li>• Maintain clear space around logo</li>
              <li>• Export at 400x400px minimum</li>
              <li>• Keep consistent across all platforms</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-body mb-2">Cover Image</h4>
            <ul className="space-y-2 text-sm text-subtle">
              <li>• Feature product photography or facility shots</li>
              <li>• Include tagline: "Frozen Today, Fresh Tomorrow"</li>
              <li>• Avoid text-heavy designs</li>
              <li>• Update quarterly with new content</li>
            </ul>
          </div>
        </div>
      </Card>

      <Card className="p-6 bg-surface">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
          Content Style and Tone
        </h3>
        <div className="space-y-4">
          <div>
            <Badge className="mb-2">Professional but Approachable</Badge>
            <p className="text-sm text-subtle">
              Maintain technical expertise while being conversational. Use industry terminology
              appropriately but remain accessible.
            </p>
          </div>
          <div>
            <Badge className="mb-2">Educational Focus</Badge>
            <p className="text-sm text-subtle">
              Share technical insights, industry trends, and educational content that demonstrates
              expertise and provides value.
            </p>
          </div>
          <div>
            <Badge className="mb-2">Customer-Centric</Badge>
            <p className="text-sm text-subtle">
              Highlight customer success stories, showcase installations, and celebrate partnerships.
            </p>
          </div>
        </div>
      </Card>

      <div>
        <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-4">
          Post Format Examples
        </h2>
        <div className="space-y-4">
          <Card className="p-4 bg-light-blue border-jackstone-blue/20">
            <p className="text-sm font-medium text-body mb-1">Product Feature Post</p>
            <p className="text-sm text-subtle italic">
              "Our VPF series vertical plate freezers deliver rapid freezing with minimal energy
              consumption. Engineered with F51 Duplex hydraulic systems for exceptional reliability
              in demanding food processing environments. #IndustrialFreezing #FoodProcessing"
            </p>
          </Card>

          <Card className="p-4 bg-light-blue border-jackstone-blue/20">
            <p className="text-sm font-medium text-body mb-1">Customer Success Post</p>
            <p className="text-sm text-subtle italic">
              "Proud to support [Customer Name] with our HPF-300 horizontal plate freezer.
              Their seafood operation now achieves 30% faster freeze times while reducing energy
              costs. Engineering excellence meets real-world results. #CustomerSuccess"
            </p>
          </Card>

          <Card className="p-4 bg-light-blue border-jackstone-blue/20">
            <p className="text-sm font-medium text-body mb-1">Industry Insight Post</p>
            <p className="text-sm text-subtle italic">
              "Technical Tuesday: The science behind plate freezer efficiency. Our latest article
              explores how aluminum plate design and hydraulic pressure optimization deliver
              superior freezing performance. Link in bio. #FoodTech #Engineering"
            </p>
          </Card>
        </div>
      </div>

      <Card className="p-6">
        <h3 className="text-xl font-heading font-semibold text-body mb-3">
          Hashtag Strategy
        </h3>
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium text-body mb-1">Brand Hashtags:</p>
            <div className="flex flex-wrap gap-2">
              <Badge tone="jackstone-blue" fill="soft" shape="pill">#Jackstone</Badge>
              <Badge tone="jackstone-blue" fill="soft" shape="pill">#FrozenTodayFreshTomorrow</Badge>
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-body mb-1">Industry Hashtags:</p>
            <div className="flex flex-wrap gap-2">
              <Badge tone="jackstone-blue" fill="soft" shape="pill">#FoodProcessing</Badge>
              <Badge tone="jackstone-blue" fill="soft" shape="pill">#IndustrialFreezing</Badge>
              <Badge tone="jackstone-blue" fill="soft" shape="pill">#PlateFreezer</Badge>
              <Badge tone="jackstone-blue" fill="soft" shape="pill">#FoodTech</Badge>
              <Badge tone="jackstone-blue" fill="soft" shape="pill">#FoodSafety</Badge>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
